import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Group, Layers, Palette } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

import { MapService, type MapDataFilters } from '@/features/map-locations';
import { MapLayersControl, MapLegend, type MapLayerId } from '@/features/map-layers';
import { GroupByControl, MAP_GROUP_BY_CONFIG, type MapCluster, type MapGroupBy } from '@/features/map-grouping';
import { TimeMachineControl, type UseTimeMachine } from '@/features/map-time-machine';
import type { CorridorDisplay, CorridorType } from '@/features/map-corridors';
import type { LocationModel } from '@/entities/location';

import { mapControlProps } from '../../lib/map-control-props';
import { MapControlPopover } from './map-control-popover';
import { MapLocationCounts } from './map-location-counts';

interface MapContainerProps {
  clusters: MapCluster[];
  iconLocations: LocationModel[];
  fitPoints: LocationModel[];
  corridors: Record<CorridorType, CorridorDisplay[]>;
  dataFilters?: MapDataFilters;
  activeLayers: MapLayerId[];
  onLayerToggle: (id: MapLayerId) => void;
  groupBy: MapGroupBy;
  onGroupByChange: (groupBy: MapGroupBy) => void;
  timeMachine: UseTimeMachine;
  weeksAgo: number;
  showTimeMachine: boolean;
  onToggleTimeMachine: () => void;
  isLoading: boolean;
}

export function MapContainer({
  clusters,
  iconLocations,
  fitPoints,
  corridors,
  dataFilters,
  activeLayers,
  onLayerToggle,
  groupBy,
  onGroupByChange,
  timeMachine,
  weeksAgo,
  showTimeMachine,
  onToggleTimeMachine,
  isLoading
}: MapContainerProps) {
  const { t } = useTranslation();
  const dimensionKey = MAP_GROUP_BY_CONFIG[groupBy].dimensionKey;
  const [mapSurface, setMapSurface] = useState<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  // On a phone the timeline is a bar across the bottom, so whatever else lives down
  // there steps up out of its way instead of sitting on top of it.
  const bottomEdge = isMobile && showTimeMachine ? 'bottom-20' : 'bottom-4';

  // Lit while the map is showing history, not just while the panel is open — with the
  // panel folded away this is the only sign that the figures are not today's.
  const isTimeMachineActive = showTimeMachine || weeksAgo > 0;

  if (fitPoints.length === 0) {
    return (
      <div className="flex min-h-[200px] w-full flex-1 items-center justify-center">
        <p className="text-muted-foreground">{t('welcome.map.empty.message')}</p>
      </div>
    );
  }

  return (
    <div ref={setMapSurface} className="relative isolate flex min-h-[200px] w-full flex-1 flex-col">
      {isLoading && (
        <div className="absolute top-2 left-14 z-[1000] rounded bg-white px-3 py-2 shadow-md">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-primary border-b-2" />
            <span className="text-sm">{t('common.loading')}</span>
          </div>
        </div>
      )}

      {MapService.getInstance()
        .getProvider()
        .render({
          clusters,
          iconLocations,
          fitPoints,
          corridors,
          dataFilters,
          dimensionLabel: dimensionKey ? t(dimensionKey) : undefined,
          weeksAgo,
          className: 'min-h-0 w-full flex-1 rounded-lg'
        })}

      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        <MapControlPopover icon={Layers} label={t('welcome.map.layers.label')} container={mapSurface}>
          <MapLayersControl activeLayers={activeLayers} onToggle={onLayerToggle} />
        </MapControlPopover>

        <MapControlPopover icon={Group} label={t('welcome.groupby.label')} container={mapSurface}>
          <GroupByControl groupBy={groupBy} onChange={onGroupByChange} />
        </MapControlPopover>
      </div>

      <div className={cn('absolute left-4 z-[1000] flex items-end gap-2', bottomEdge)}>
        <div className="rounded-lg bg-white/90 p-2 shadow-lg">
          <MapLocationCounts locations={fitPoints} />
          {/* The four labelled colours are the bulk of this panel; on a phone they
              fold behind a control and the compact counts stay in view. */}
          {!isMobile && (
            <div className="mt-2 border-t pt-2">
              <MapLegend />
            </div>
          )}
        </div>

        {isMobile && (
          <MapControlPopover icon={Palette} label={t('welcome.map.legend.label')} container={mapSurface}>
            <MapLegend />
          </MapControlPopover>
        )}
      </div>

      <Button
        {...mapControlProps(isTimeMachineActive)}
        className={cn(mapControlProps(isTimeMachineActive).className, 'absolute right-4 z-[1000]', bottomEdge)}
        onClick={onToggleTimeMachine}
        aria-label={t('welcome.map.timeline.label')}
        title={t(showTimeMachine ? 'welcome.map.timeline.active.desc' : 'welcome.map.timeline.enable.desc')}>
        <Clock className="h-4 w-4" />
      </Button>

      {showTimeMachine && <TimeMachineControl timeMachine={timeMachine} />}
    </div>
  );
}
